/**
 * Cryptographic Token Utility for Temporary Customer Reservation Access
 * Uses Web Crypto HMAC-SHA256 (compatible with Cloudflare Workers, Node.js 18+, and browsers).
 */

const DEFAULT_SECRET = 'uniform-loop-immingham-rat-secret-2026';

export interface ReservationTokenPayload {
  sub: string;        // Reservation Document ID
  ref: string;        // Reservation Reference (e.g. UL48291)
  emh: string;        // SHA-256 hash of normalized lowercase email
  iat: number;        // Issued at (seconds)
  exp: number;        // Expires at (seconds, 15 min lifetime)
  jti: string;        // Unique Nonce
}

/**
 * Normalizes email to lowercase trimmed string
 */
export function normalizeEmail(email: string): string {
  return (email || '').trim().toLowerCase();
}

/**
 * Computes SHA-256 hex string of a normalized email
 */
export async function hashEmail(email: string): Promise<string> {
  const normalized = normalizeEmail(email);
  const data = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

function base64UrlEncode(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

async function getHmacKey(secret: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  return crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify']
  );
}

/**
 * Generates a signed 15-minute Reservation Action Token (RAT)
 */
export async function generateReservationToken(
  reservationId: string,
  reservationCode: string,
  email: string,
  secret = DEFAULT_SECRET,
  expiresInSeconds = 900 // 15 minutes
): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const emh = await hashEmail(email);

  const header = { alg: 'HS256', typ: 'RAT' };
  const payload: ReservationTokenPayload = {
    sub: reservationId,
    ref: reservationCode.trim().toUpperCase(),
    emh,
    iat: now,
    exp: now + expiresInSeconds,
    jti: Math.random().toString(36).substring(2, 15)
  };

  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));
  const dataToSign = `${encodedHeader}.${encodedPayload}`;

  const key = await getHmacKey(secret);
  const signatureBuffer = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(dataToSign)
  );

  const signatureBytes = new Uint8Array(signatureBuffer);
  let binarySig = '';
  for (let i = 0; i < signatureBytes.byteLength; i++) {
    binarySig += String.fromCharCode(signatureBytes[i]);
  }
  const encodedSignature = btoa(binarySig)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return `${dataToSign}.${encodedSignature}`;
}

/**
 * Verifies a Reservation Action Token (RAT)
 */
export async function verifyReservationToken(
  token: string,
  secret = DEFAULT_SECRET
): Promise<{ valid: boolean; payload?: ReservationTokenPayload; error?: string }> {
  try {
    if (!token || typeof token !== 'string') {
      return { valid: false, error: 'Token missing' };
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return { valid: false, error: 'Malformed token structure' };
    }

    const [encodedHeader, encodedPayload, encodedSignature] = parts;
    const dataToVerify = `${encodedHeader}.${encodedPayload}`;

    // Decode signature
    let base64Sig = encodedSignature.replace(/-/g, '+').replace(/_/g, '/');
    while (base64Sig.length % 4) {
      base64Sig += '=';
    }
    const binarySig = atob(base64Sig);
    const signatureBytes = new Uint8Array(binarySig.length);
    for (let i = 0; i < binarySig.length; i++) {
      signatureBytes[i] = binarySig.charCodeAt(i);
    }

    const key = await getHmacKey(secret);
    const isValid = await crypto.subtle.verify(
      'HMAC',
      key,
      signatureBytes,
      new TextEncoder().encode(dataToVerify)
    );

    if (!isValid) {
      return { valid: false, error: 'Invalid token signature' };
    }

    const payloadJson = base64UrlDecode(encodedPayload);
    const payload: ReservationTokenPayload = JSON.parse(payloadJson);

    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now) {
      return { valid: false, error: 'Reservation access token has expired (15m limit)' };
    }

    return { valid: true, payload };
  } catch (err: any) {
    return { valid: false, error: err.message || 'Token verification failed' };
  }
}

/**
 * Generates a cryptographically random, unguessable 256-bit reply token string
 */
export function generateReplyToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Computes the SHA-256 hex hash of a raw reply token for safe storage and verification
 */
export async function hashReplyToken(token: string): Promise<string> {
  const normalized = (token || '').trim();
  const data = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

